import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, apiUrl, ensureDeviceFingerprint } from "../lib/api";
import { buildWatermarkText } from "@dataroom/shared";
import { downloadDocument } from "../lib/fetchDocument";
import { getViewerEmail, getViewerSessionId, getViewerViewId, setViewerViewId } from "../lib/viewerSession";
import { DocumentPreview } from "../components/DocumentPreview";
import { PreviewErrorBoundary } from "../components/PreviewErrorBoundary";
import { BrandLoader } from "../components/BrandLoader";
import { useDelayedLoader } from "../hooks/useDelayedLoader";
import { IconClose, IconDownload } from "../components/Icons";
import { DEFAULT_VIEWER_LOGO } from "../components/ViewerPortalHeader";

type GateMeta = {
  roomName: string;
  logoUrl?: string;
  watermarkEnabled: boolean;
  allowDownload: boolean;
  branding?: { tokens_json?: string };
};

type Doc = {
  id: string;
  filename: string;
  mime_type?: string;
  display_name?: string;
};

function docLabel(d: Doc) {
  return d.display_name?.trim() || d.filename;
}

export function StandalonePreviewPage() {
  const { token = "", documentId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState<GateMeta | null>(null);
  const [gate, setGate] = useState<"loading" | "content" | "error">("loading");
  const [docLoading, setDocLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(() => searchParams.get("sid") || getViewerSessionId(token));
  const [viewId, setViewId] = useState(() => getViewerViewId(token));
  const [email] = useState(() => getViewerEmail(token));
  const [doc, setDoc] = useState<Doc | null>(null);
  const [previewPage, setPreviewPage] = useState({ current: 1, total: 0 });
  const showGateLoader = useDelayedLoader(gate === "loading" || docLoading);
  const eventBuffer = useMemo(
    () => [] as Array<{ documentId: string; pageNumber: number; dwellMs: number; event: string }>,
    [],
  );

  const flushEvents = useCallback(
    async (keepalive = false) => {
      if (!sessionId || !eventBuffer.length) return;
      const batch = eventBuffer.splice(0, eventBuffer.length);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Viewer-Session": sessionId,
      };
      const fp = localStorage.getItem("deviceFp");
      if (fp) headers["X-Device-Fingerprint"] = fp;
      await fetch(apiUrl(`/api/v1/viewer/${token}/events`), {
        method: "POST",
        headers,
        body: JSON.stringify({ events: batch }),
        keepalive,
      }).catch(() => undefined);
    },
    [eventBuffer, sessionId, token],
  );

  useEffect(() => {
    ensureDeviceFingerprint();
    if (!sessionId) {
      setGate("error");
      setError("Session expired. Return to the Space and open the file again.");
      return;
    }
    api<GateMeta>(`/api/v1/viewer/${token}`, { admin: false })
      .then((m) => {
        setMeta(m);
        setGate("content");
      })
      .catch((e) => {
        setError(String((e as Error).message || e));
        setGate("error");
      });
  }, [token, sessionId]);

  useEffect(() => {
    if (gate !== "content" || !sessionId || !documentId) return;
    setDocLoading(true);
    api<{ documents: Doc[]; viewId: string }>(`/api/v1/viewer/${token}/files`, {
      admin: false,
      sessionId,
    })
      .then((res) => {
        if (res.viewId) {
          setViewId(res.viewId);
          setViewerViewId(token, res.viewId);
        }
        const hit = res.documents.find((d) => d.id === documentId);
        if (!hit) {
          setGate("error");
          setError("This file is no longer available.");
          return;
        }
        setDoc(hit);
      })
      .catch((e) => {
        setError(String((e as Error).message || e));
        setGate("error");
      })
      .finally(() => setDocLoading(false));
  }, [gate, sessionId, token, documentId]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") flushEvents(true).catch(() => undefined);
    };
    const onUnload = () => flushEvents(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
      flushEvents(true).catch(() => undefined);
    };
  }, [flushEvents]);

  useEffect(() => {
    const t = window.setInterval(() => {
      flushEvents().catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(t);
  }, [flushEvents]);

  const brandPrimary =
    (() => {
      try {
        return JSON.parse(meta?.branding?.tokens_json || "{}").primary as string | undefined;
      } catch {
        return undefined;
      }
    })() ?? "#072366";

  const logoSrc = meta?.logoUrl || DEFAULT_VIEWER_LOGO;
  const contentPath = `/api/v1/viewer/${token}/files/${documentId}/content`;
  const downloadPath = `/api/v1/viewer/${token}/files/${documentId}/download`;

  const downloadFile = () => {
    const watermarkText = meta?.watermarkEnabled
      ? buildWatermarkText({ email, viewId, timestamp: new Date().toISOString() })
      : undefined;
    downloadDocument(downloadPath, doc?.filename || "download", { sessionId, watermarkText }).catch((e) =>
      setError(String((e as Error).message || e)),
    );
  };

  if (gate === "error") {
    return (
      <div className="preview-standalone-page preview-standalone-page--loading">
        <div className="card preview-standalone__error-card" style={{ maxWidth: 420, padding: 20, textAlign: "center" }}>
          <p role="alert">{error || "Could not open this file."}</p>
          <div className="row toolbar" style={{ justifyContent: "center", marginTop: 12 }}>
            <Link to={`/v/${token}`} className="secondary" style={{ textDecoration: "none" }}>
              Return to Space
            </Link>
            <button type="button" className="secondary" onClick={() => window.close()}>
              Close tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (gate === "loading" || docLoading || !doc) {
    return (
      <div className="preview-standalone-page preview-standalone-page--loading">
        {showGateLoader ? <BrandLoader size="lg" label="Opening preview…" /> : null}
      </div>
    );
  }

  return (
    <div className="preview-standalone-page" style={{ "--preview-brand": brandPrimary } as React.CSSProperties}>
      <header className="preview-standalone__bar">
        <div className="preview-standalone__brand">
          <img className="preview-standalone__logo" src={logoSrc} alt="" />
          <div className="preview-standalone__meta">
            <strong className="preview-standalone__title">{docLabel(doc)}</strong>
            <span className="preview-standalone__room">{meta?.roomName}</span>
          </div>
        </div>
        <div className="preview-standalone__status">
          {previewPage.total > 0 ? (
            <span className="preview-standalone__page">
              {previewPage.current} / {previewPage.total}
            </span>
          ) : null}
          {meta?.watermarkEnabled ? <span className="preview-standalone__tag">Watermarked</span> : null}
        </div>
        <div className="preview-standalone__actions">
          {meta?.allowDownload ? (
            <button type="button" className="preview-standalone__action" onClick={downloadFile} aria-label="Download">
              <IconDownload aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            className="preview-standalone__action"
            onClick={() => window.close()}
            aria-label="Close preview"
          >
            <IconClose aria-hidden />
          </button>
        </div>
      </header>

      <main className="preview-standalone__main">
        <PreviewErrorBoundary>
          <DocumentPreview
            key={documentId}
            layout="standalone"
            url={contentPath}
            filename={doc.filename}
            mimeType={doc.mime_type}
            sessionId={sessionId}
            documentId={documentId}
            watermarkEmail={meta?.watermarkEnabled ? email || undefined : undefined}
            viewId={viewId}
            onCurrentPageChange={(current, total) => setPreviewPage({ current, total })}
            onPageEvent={(ev) => {
              eventBuffer.push({
                documentId,
                pageNumber: ev.pageNumber,
                dwellMs: ev.dwellMs,
                event: ev.event,
              });
            }}
          />
        </PreviewErrorBoundary>
      </main>

      <footer className="preview-standalone__footer">
        <Link to={`/v/${encodeURIComponent(token)}/privacy`}>Privacy</Link>
        {viewId ? <span>View ID {viewId}</span> : null}
      </footer>
    </div>
  );
}
